'use client';

import { createElement, useEffect, useRef, type ReactNode } from 'react';

// Homepage motion. Everything renders in its finished state on the server; a block
// is only "armed" (set back to its starting pose) when it is still below the fold
// after hydration and the visitor has not asked for reduced motion.
export function motionAllowed() {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
export function belowFold(element: Element) {
  return element.getBoundingClientRect().top > window.innerHeight * 0.9;
}
export function onceInView(element: Element, run: () => void, rootMargin = '0px 0px -15% 0px') {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      run();
    },
    { rootMargin },
  );
  observer.observe(element);
  return () => observer.disconnect();
}

/** Reveals a list's children one after another the first time it scrolls into view. */
export function Stagger({
  tag,
  gap,
  className,
  label,
  children,
}: {
  tag: 'div' | 'ul';
  gap: number;
  className?: string;
  label?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const list = ref.current;
    if (!list || !motionAllowed() || !belowFold(list)) return;
    list.dataset.armed = '';
    const timers: number[] = [];
    const stop = onceInView(list, () =>
      Array.from(list.children).forEach((child, index) =>
        timers.push(window.setTimeout(() => ((child as HTMLElement).dataset.in = ''), index * gap)),
      ),
    );
    return () => {
      stop();
      timers.forEach(clearTimeout);
    };
  }, [gap]);
  return createElement(tag, { ref, className, 'aria-label': label }, children);
}

/** Lifts a block slightly into place the first time it scrolls into view. */
export function Rise({ className, children }: { className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const block = ref.current;
    if (!block || !motionAllowed() || !belowFold(block)) return;
    block.dataset.pre = '';
    return onceInView(block, () => delete block.dataset.pre, '0px 0px -8% 0px');
  }, []);
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
